import React, { forwardRef, KeyboardEventHandler, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Button, useTheme } from '@mui/material';
import usePlaceAutocomplete, {
    AddressFormatted,
    PlaceType,
} from '../../../hooks/usePlaceAutocomplete';

export type GoogleMapAutocompleteProps = {
    inputValue: string;
    placeholder: string;
    onChange: (formattedAddress: AddressFormatted) => void;
    onInputChange?: () => void;
    onKeyUp: KeyboardEventHandler<HTMLDivElement>;
    minLengthDebounce?: number;
    hasError?: boolean;
    isFrenchAddress: boolean;
};

export const GoogleMapAutocomplete: React.FunctionComponent<GoogleMapAutocompleteProps> =
    forwardRef(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (props: GoogleMapAutocompleteProps, ref) => {
            const {
                inputValue,
                placeholder,
                onChange,
                onKeyUp,
                minLengthDebounce = 3,
                hasError = false,
                isFrenchAddress = true,
                onInputChange,
            } = props;
            const [value, setValue] = useState<PlaceType | string | null>(null);
            const [searchTerm, setSearchTerm] = useState('');
            const [options, setOptions] = useState<readonly PlaceType[]>([]);
            const [loading, setLoading] = useState<boolean>(false);
            const { getPlaceDetail, loadScript, fetchSuggestions, formatAddress } =
                usePlaceAutocomplete();
            const theme = useTheme();
            const { palette } = theme;

            const handleSelectPlace = async (option: PlaceType) => {
                const details = await getPlaceDetail(option.place_id);

                if (onChange) {
                    onChange(formatAddress(details));
                }
            };

            if (typeof window !== 'undefined') {
                loadScript();
            }

            useEffect(() => {
                setValue(inputValue);
            }, [inputValue]);

            useEffect(() => {
                if (searchTerm.length < minLengthDebounce) {
                    return () => {};
                }

                let active = true;
                setLoading(true);

                let componentRestrictions;
                if (isFrenchAddress) {
                    componentRestrictions = {
                        componentRestrictions: {
                            country: [
                                'fr', // France métropole
                                'gp', // Guadeloupe
                                'mq', // Martinique
                                'gf', // Guyane
                                're', // La Réunion
                                'yt', // Mayotte
                                'pf', // Polynésie française
                                'nc', // Nouvelle-Calédonie
                                'bl', // Saint-Barthélemy
                                'mf', // Saint-Martin
                                'pm', // Saint-Pierre-et-Miquelon
                                'wf', // Wallis-et-Futuna
                            ],
                        },
                    };
                }

                fetchSuggestions(
                    {
                        input: searchTerm,
                        types: ['address'],
                        ...componentRestrictions,
                    },
                    (results?: readonly PlaceType[]) => {
                        if (active) {
                            let newOptions: readonly PlaceType[] = [];

                            if (results) {
                                newOptions = [...newOptions, ...results];
                            }

                            setOptions(newOptions);
                            setLoading(false);
                        }
                    },
                );

                return () => {
                    active = false;
                };
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [value, searchTerm, fetchSuggestions]);

            return (
                <Autocomplete
                    freeSolo
                    getOptionLabel={(option) =>
                        typeof option === 'string' ? option : option.description
                    }
                    filterOptions={(x) => x}
                    options={options}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    value={value}
                    loading={loading}
                    loadingText="Chargement..."
                    clearIcon={null}
                    noOptionsText="Aucun résultat pour votre recherche"
                    onChange={(
                        event: React.FormEvent<HTMLInputElement>,
                        newValue: PlaceType | null,
                    ) => {
                        if (newValue) {
                            setOptions(newValue ? [newValue, ...options] : options);
                            setValue(newValue);
                        } else {
                            setOptions([]);
                            setValue(null);
                        }
                    }}
                    onInputChange={(event, newSearchTerm) => {
                        setSearchTerm(newSearchTerm);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={placeholder}
                            fullWidth
                            error={hasError}
                            onKeyUp={onKeyUp}
                            onChange={onInputChange}
                        />
                    )}
                    renderOption={(childProps, option) => {
                        const { key, ...optionProps } = childProps;
                        const label = option.description;

                        return (
                            <li
                                key={key}
                                {...optionProps}
                            >
                                <Button
                                    sx={{ textAlign: 'left', color: palette.text.primary }}
                                    onClick={() => handleSelectPlace(option)}
                                >
                                    {label}
                                </Button>
                            </li>
                        );
                    }}
                />
            );
        },
    );
